import type { WorkflowType } from '@/constants/video-generation-presets';

/**
 * Video Agent "use-case" presets — landing-page configs reached via the
 * `?template=<slug>` query param on /create-video (e.g.
 * `/create-video?template=real-estate-marketing-video`).
 *
 * A preset pre-selects a workflow (e.g. "Your own media") and swaps the page
 * title / description / SEO so the same Video Agent form doubles as a focused,
 * SEO-friendly landing page for a specific use case. It only seeds defaults —
 * the user can still change anything afterwards.
 *
 * The param is named `template` (not `preset`) on purpose: `preset` already
 * refers to the visual *style* preset (cinematic, anime, …) inside the form.
 */
export interface VideoAgentPreset {
  /** URL slug — matches the `?template=` value. */
  slug: string;
  /** Workflow tab to pre-select. */
  workflowType: WorkflowType;
  /** H1 shown in the page header. */
  title: string;
  /** Sub-header paragraph under the H1. */
  description: string;
  /** SEO <title>. Falls back to `title`. */
  seoTitle?: string;
  /** SEO meta description. Falls back to `description`. */
  seoDescription?: string;
}

export const VIDEO_AGENT_PRESETS: Record<string, VideoAgentPreset> = {
  'real-estate-marketing-video': {
    slug: 'real-estate-marketing-video',
    workflowType: 'your-own-media',
    title: 'Real Estate Marketing Video',
    description:
      'Create stunning real estate videos automatically with our Real Estate Marketing Video AI. Just upload photos & text — get pro video tours instantly.',
  },
};

export function getVideoAgentPreset(
  slug: string | string[] | undefined,
): VideoAgentPreset | undefined {
  if (typeof slug !== 'string') return undefined;
  return VIDEO_AGENT_PRESETS[slug];
}
